// features/visibility/campaign-details/campaign-details-href.ts
// ============================================================================
// THE WAY INTO CAMPAIGN DETAILS FROM ANOTHER PAGE, AND THE WAY BACK
// ============================================================================
// Campaign details is a panel on Operations' road (`/operations?playbook=<id>&details=true`). A link from another page
// (Review Content's "Two approvals on this campaign.", Team & roles' Agencies card, Integrations' Approvals in Slack)
// says where it was opened from (`back`) and may open it on its Approvals card (`card=approvals`):
//
//   /operations?playbook=1&details=true&card=approvals&back=%2Freview%2Fcontent%3Fid%3Dcontent%3A9bc…
//
// Its close (✕, Escape, the phone's back arrow) then leads back there, named for the page ("Back to Review Content").
// `back` is kept only for an address inside the app on Review (`/review/<kind>…`) or Settings (`/settings…`): a full URL,
// `//host`, a backslash, a space and every other path are dropped, so a link can never send her off the app or to a page
// it doesn't name. Pure, and free of imports: Operations' and Settings' search validators (the entry chunk) read it.

/** The card Campaign details may open on. */
export const DETAILS_CARDS = ['approvals', 'budget'] as const; // 'budget': the operator Money's budget door (BudgetLanding)
export type DetailsCard = (typeof DETAILS_CARDS)[number];
export const isDetailsCard = (value: unknown): value is DetailsCard => DETAILS_CARDS.some((card) => card === value);

/** The pages Campaign details leads back to, by their real names. */
export type DetailsBackPage = 'Review Content' | 'Review' | 'Team & roles' | 'Integrations' | 'Draper Knowledge Base' | 'Settings' | 'Money';

export interface DetailsBack {
  /** An address inside the app, as the link gave it. */
  to: string;
  page: DetailsBackPage;
}

/** The longest way back kept (a draft's address with Settings' own way back inside it is far shorter). */
const BACK_MAX = 1024;
/** Review: one review page, and any search with no space, fragment or backslash. */
const REVIEW_BACK = /^\/review\/([a-z][a-z-]{0,31})(\?[^\s#\\]*)?$/;
/** Settings: any section, the same way. */
const SETTINGS_BACK = /^\/settings(\?[^\s#\\]*)?$/;

/** Settings' sections Campaign details names on its way back (any other reads "Settings"). */
const SETTINGS_PAGES: Readonly<Record<string, DetailsBackPage>> = { team: 'Team & roles', integrations: 'Integrations', draper: 'Draper Knowledge Base' };

/** The section a Settings address opens, read the way the address writes it; null when it names none. */
function sectionOf(query: string | undefined): string | null {
  if (!query) return null;
  for (const pair of query.slice(1).split('&')) {
    const at = pair.indexOf('=');
    if (at > 0 && pair.slice(0, at) === 'section') return pair.slice(at + 1);
  }
  return null;
}

/**
 * The way back a link may ask Campaign details for, or undefined (dropped): an address on Review (`/review/content…` →
 * "Review Content", any other review page → "Review") or Settings (`?section=team` → "Team & roles", `integrations` →
 * "Integrations", `draper` → "Draper Knowledge Base", else "Settings"). Pure.
 */
export function detailsBackOf(value: unknown): DetailsBack | undefined {
  if (typeof value !== 'string' || value.length > BACK_MAX || !value.startsWith('/') || value.startsWith('//')) return undefined;
  // Control characters never belong in an address.
  if ([...value].some((ch) => ch.charCodeAt(0) < 32 || ch.charCodeAt(0) === 127)) return undefined;
  const review = REVIEW_BACK.exec(value);
  if (review) return { to: value, page: review[1] === 'content' ? 'Review Content' : 'Review' };
  const settings = SETTINGS_BACK.exec(value);
  if (settings) return { to: value, page: SETTINGS_PAGES[sectionOf(settings[1]) ?? ''] ?? 'Settings' };
  // The operator's Money: the Overview's budget line, with the campaign and filters he left.
  if (/^\/money(\?[^\s#\\]*)?$/.test(value)) return { to: value, page: 'Money' };
  return undefined;
}

/** The close control's name on a way back: "Back to Review Content". */
export const backWords = (back: Pick<DetailsBack, 'page'>): string => `Back to ${back.page}`;

/**
 * Campaign details of this campaign, from another page: `back` the address she is on (kept only when `detailsBackOf`
 * keeps it), `card` the card it opens on. `/operations?playbook=1&details=true&card=approvals&back=<encoded url>`.
 */
export function campaignDetailsHref(playbookId: number, options: { back?: string | null; card?: DetailsCard | null } = {}): string {
  const back = detailsBackOf(options.back)?.to;
  return `/operations?playbook=${playbookId}&details=true${options.card ? `&card=${options.card}` : ''}${back ? `&back=${encodeURIComponent(back)}` : ''}`;
}

/** Where Campaign details came from, kept for the visit once the address forgets it. */
export interface DetailsDoor {
  back: DetailsBack | null;
  card: DetailsCard | null;
}

/** The door a parsed address carries (Operations' `back` and `card`); null when it carries neither. */
export function detailsDoorOf(search: { back?: string; card?: DetailsCard }): DetailsDoor | null {
  const back = detailsBackOf(search.back) ?? null;
  const card = search.card ?? null;
  return back || card ? { back, card } : null;
}
